import { useCallback } from "react";
import type { Collab } from "@cabal/shared/domain/session/collab/types.js";
import {
  ChatUnreadDiagnosticBranch,
  ChatUnreadDiagnosticComponent,
  ChatUnreadDiagnosticEvent,
  ChatUnreadDiagnosticPhase,
  logChatUnreadDiagnostic,
} from "@/utils/chatUnreadDiagnostics";
import { parseDateOrNull } from "./session-date-selectors";
import { selectLatestCollabByIds } from "./read-tracking-selectors";
import type {
  SessionReadMarkerActions,
  UpdateLocalProductReadMarker,
} from "./use-session-read-tracking.types";

interface UseSessionReadMarkerActionsInput {
  chatMessageIds: readonly string[];
  chatMessages: Record<string, Collab>;
  collabs: Record<string, Collab>;
  collabsByProduct: Record<string, string[]>;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}

export function useSessionReadMarkerActions(
  input: UseSessionReadMarkerActionsInput,
): SessionReadMarkerActions {
  const {
    chatMessageIds,
    chatMessages,
    collabs,
    collabsByProduct,
    updateLocalProductReadMarker,
  } = input;
  const markAllChatAsRead = useMarkAllChatAsRead({
    chatMessageIds,
    chatMessages,
    updateLocalProductReadMarker,
  });
  const markAllCollabsAsRead = useMarkAllCollabsAsRead({
    collabs,
    collabsByProduct,
    updateLocalProductReadMarker,
  });
  const markChatReadUpTo = useMarkChatReadUpTo({
    chatMessages,
    updateLocalProductReadMarker,
  });
  const markCollabsReadUpTo = useMarkCollabsReadUpTo({
    collabs,
    collabsByProduct,
    updateLocalProductReadMarker,
  });

  return {
    markAllChatAsRead,
    markAllCollabsAsRead,
    markChatReadUpTo,
    markCollabsReadUpTo,
  };
}

function useMarkAllChatAsRead(input: {
  chatMessageIds: readonly string[];
  chatMessages: Record<string, Collab>;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}): () => void {
  const { chatMessageIds, chatMessages, updateLocalProductReadMarker } = input;

  return useCallback((): void => {
    const latestChatMessage = selectLatestCollabByIds({
      collabIds: chatMessageIds,
      collabs: chatMessages,
    });

    logChatUnreadDiagnostic({
      component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
      event: ChatUnreadDiagnosticEvent.LocalReadMarker,
      props: {
        phase: ChatUnreadDiagnosticPhase.MarkAllChatEvaluate,
        selectedBranch: latestChatMessage
          ? ChatUnreadDiagnosticBranch.Update
          : ChatUnreadDiagnosticBranch.SkipNoLatestMessage,
        chatMessageCount: chatMessageIds.length,
        latestMessageId: latestChatMessage?.id ?? null,
        latestMessageCreatedAt: latestChatMessage?.createdAt ?? null,
      },
    });

    if (!latestChatMessage) return;

    updateLocalProductReadMarker({
      productId: null,
      collabsReadAt: parseDateOrNull(latestChatMessage.createdAt),
    });
  }, [chatMessageIds, chatMessages, updateLocalProductReadMarker]);
}

function useMarkAllCollabsAsRead(input: {
  collabs: Record<string, Collab>;
  collabsByProduct: Record<string, string[]>;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}): (productId: string) => void {
  const { collabs, collabsByProduct, updateLocalProductReadMarker } = input;

  return useCallback(
    (productId: string): void => {
      const collabIds = collabsByProduct[productId] ?? [];
      if (collabIds.length === 0) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
          event: ChatUnreadDiagnosticEvent.LocalReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.MarkAllProductCollabsEvaluate,
            selectedBranch: ChatUnreadDiagnosticBranch.SkipEmptyProductCollabs,
            productId,
          },
        });
        return;
      }

      const latestProductCollab = selectLatestCollabByIds({
        collabIds,
        collabs,
      });

      logChatUnreadDiagnostic({
        component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
        event: ChatUnreadDiagnosticEvent.LocalReadMarker,
        props: {
          phase: ChatUnreadDiagnosticPhase.MarkAllProductCollabsEvaluate,
          selectedBranch: latestProductCollab
            ? ChatUnreadDiagnosticBranch.Update
            : ChatUnreadDiagnosticBranch.SkipNoLatestCollab,
          productId,
          collabCount: collabIds.length,
          latestMessageId: latestProductCollab?.id ?? null,
          latestMessageCreatedAt: latestProductCollab?.createdAt ?? null,
        },
      });

      if (!latestProductCollab) return;

      updateLocalProductReadMarker({
        productId,
        collabsReadAt: parseDateOrNull(latestProductCollab.createdAt),
      });
    },
    [collabs, collabsByProduct, updateLocalProductReadMarker],
  );
}

function useMarkChatReadUpTo(input: {
  chatMessages: Record<string, Collab>;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}): (messageId: string) => void {
  const { chatMessages, updateLocalProductReadMarker } = input;

  return useCallback(
    (messageId: string): void => {
      const target = chatMessages[messageId];
      logChatUnreadDiagnostic({
        component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
        event: ChatUnreadDiagnosticEvent.LocalReadMarker,
        props: {
          phase: ChatUnreadDiagnosticPhase.MarkChatReadUpToEvaluate,
          selectedBranch: target
            ? ChatUnreadDiagnosticBranch.Update
            : ChatUnreadDiagnosticBranch.SkipMissingTarget,
          messageId,
          targetCreatedAt: target?.createdAt ?? null,
        },
      });

      if (!target) return;

      updateLocalProductReadMarker({
        productId: null,
        collabsReadAt: parseDateOrNull(target.createdAt),
      });
    },
    [chatMessages, updateLocalProductReadMarker],
  );
}

function useMarkCollabsReadUpTo(input: {
  collabs: Record<string, Collab>;
  collabsByProduct: Record<string, string[]>;
  updateLocalProductReadMarker: UpdateLocalProductReadMarker;
}): (productId: string, messageId: string) => void {
  const { collabs, collabsByProduct, updateLocalProductReadMarker } = input;

  return useCallback(
    (productId: string, messageId: string): void => {
      const collabIds = collabsByProduct[productId] ?? [];
      if (collabIds.length === 0) {
        logChatUnreadDiagnostic({
          component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
          event: ChatUnreadDiagnosticEvent.LocalReadMarker,
          props: {
            phase: ChatUnreadDiagnosticPhase.MarkProductCollabsReadUpToEvaluate,
            selectedBranch: ChatUnreadDiagnosticBranch.SkipEmptyProductCollabs,
            productId,
            messageId,
          },
        });
        return;
      }

      const target = collabs[messageId];
      logChatUnreadDiagnostic({
        component: ChatUnreadDiagnosticComponent.SessionReadMarkerActions,
        event: ChatUnreadDiagnosticEvent.LocalReadMarker,
        props: {
          phase: ChatUnreadDiagnosticPhase.MarkProductCollabsReadUpToEvaluate,
          selectedBranch: target
            ? ChatUnreadDiagnosticBranch.Update
            : ChatUnreadDiagnosticBranch.SkipMissingTarget,
          productId,
          messageId,
          collabCount: collabIds.length,
          targetInProduct: collabIds.includes(messageId),
          targetCreatedAt: target?.createdAt ?? null,
        },
      });

      if (!target) return;

      updateLocalProductReadMarker({
        productId,
        collabsReadAt: parseDateOrNull(target.createdAt),
      });
    },
    [collabs, collabsByProduct, updateLocalProductReadMarker],
  );
}
